// hooks

// types
import type { DealHistory } from '@makemydeal/dr-dash-types';
import type { FC } from 'react';
import { useMemo } from 'react';
// selectors
import * as accessoriesSelectors from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import * as calculationDetailsSelectors from '@makemydeal/dr-dash-store/selectors/calculationDetailsSelectors';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as vehicleProtectionUtils from '@makemydeal/dr-dash-store/utils/vehicleProtectionUtils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';

// constants
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// components
import type { LineItemCategory } from '@makemydeal/dr-platform-types';
import { TaxNames } from '@makemydeal/dr-platform-types';
import DealSummaryItem from '../common/DealSummaryItem';
import LineItemWithChildren from '../common/LineItemWithChildren';
import {
    convertAccessoryToLineItem,
    convertVppProductToLineItem,
    getLabelStyles,
    lineItemWithChildrenAdaptor,
    removeTaxesByName
} from '../utils/transform';

// styles
import {
    defaultGridProps,
    labelStyles as defaultLabelStyles,
    valueStyles as defaultValueStyles,
    detailedViewGridProps,
    detailedViewLabelStyles,
    detailedViewValueStyles,
    expandibleAreaStyles,
    expandibleButtonStyles,
    secondaryValueStyles
} from '../utils/styles';

import { Grid } from '@interstate/components/Grid';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useMediaQuery } from 'react-responsive';
import { useDealSummary } from '../../common/DealSummaryProvider';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

type DueAtSigningProps = {
    deal?: DealHistory;
};

const Tax_Object = {
    key: 'taxName',
    label: 'taxName',
    value: 'taxAmount'
};

const DueAtSigning: FC<DueAtSigningProps> = ({ deal }) => {
    const useDealRootSelector = createDealRootSelector(deal, 'DueAtSigning');
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();

    const totalAmountDue = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getDueAtSigningFromDueInAdvance, 'dueAtSigningFromDueInAdvance', {
            context: 'leaseBreakdown',
            contextArg: deal
        }),
        deal
    );
    const formattedTotalAmountDue = formatDollarsAndCents(totalAmountDue);

    const initialIsFirstPaymentCapitalized = useTrackedSelector(calculationDetailsSelectors.getIsFirstPaymentCapitalized, {
        component: 'DueAtSigning',
        field: 'isFirstPaymentCapitalized'
    });
    const paymentFirstPaymentCapitalized = useTrackedSelector(offerReduxSelectors.getFirstPaymentCapitalized, {
        component: 'DueAtSigning',
        field: 'firstPaymentCapitalized'
    });
    const firstPaymentCapitalized = paymentFirstPaymentCapitalized ?? initialIsFirstPaymentCapitalized;

    const monthlyPayment = useHeldValue(useDealRootSelector(offerSelectors.getMonthlyPayment, 'monthlyPayment'), deal);
    const isOnePayLeaseFirstPaymentEnabled = useTrackedSelector(featureToggleSelectors.isOnePayLeaseFirstPaymentEnabled, {
        component: 'DueAtSigning',
        field: 'isOnePayLeaseFirstPaymentEnabled'
    });
    const isOnePayLease = useDealRootSelector(offerReduxSelectors.isOnePayLease, 'isOnePayLease');
    const firstPaymentAmountRaw = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getFirstPaymentAmount, 'firstPaymentAmount'),
        deal
    );
    const baseMonthlyPaymentTotal = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getBaseMonthlyPaymentTotal, 'baseMonthlyPaymentsTotal'),
        deal
    );
    const firstPaymentAmount =
        isOnePayLeaseFirstPaymentEnabled && isOnePayLease ? firstPaymentAmountRaw ?? baseMonthlyPaymentTotal : monthlyPayment;

    // Taxes
    const isCapReductionTaxCapitalizedIndicator = useTrackedSelector(offerReduxSelectors.getCapReductionTaxCapitalizedIndicator, {
        component: 'DueAtSigning',
        field: 'capReductionTaxCapitalizedIndicator'
    });
    const upfrontTaxTotal = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getUpfrontTaxTotal, {
            component: 'DueAtSigning',
            field: 'upfrontTaxTotal'
        })
    );
    const hasManualTax = useTrackedSelector(offerReduxSelectors.hasManualTotalTax, {
        component: 'DueAtSigning',
        field: 'manualTotalTax'
    });
    const hasManualUpfrontTotalTax = hasManualTax && upfrontTaxTotal !== 0;
    const getFlatOrItemizedTaxes = useTrackedSelector(offerSelectors.getFlatOrItemizedTaxes, {
        component: 'DueAtSigning',
        field: 'flatOrItemizedTaxes'
    });
    const manualTotalTax = useTrackedSelector(offerSelectors.getFlatTaxForDisplay, {
        component: 'DueAtSigning',
        field: 'flatTaxForDisplay'
    });
    const manualTaxTotalDisplay = lineItemWithChildrenAdaptor(manualTotalTax, Tax_Object);
    const isTaxCapped = useTrackedSelector(offerReduxSelectors.isTaxCapped, { component: 'DueAtSigning', field: 'taxCapped' });

    // Show upfront taxes: Advanced/Flat when !isTaxCapped, CRT when !isCapReductionTaxCapitalizedIndicator
    const shouldShowUpfrontTaxItems = !isTaxCapped || !isCapReductionTaxCapitalizedIndicator;
    const taxesToRemove = [
        TaxNames.MonthlyUseTax, // Always remove MUT (it's monthly, not upfront)
        ...getFlatOrItemizedTaxes.filter((tax) => tax.monthlyTax != null).map((tax) => tax.taxName), // Remove any monthly taxes
        ...(isTaxCapped ? [TaxNames.Flat, TaxNames.Advanced] : []), // Remove Flat/Advanced if capitalized
        ...(isCapReductionTaxCapitalizedIndicator ? [TaxNames.CapCostReduction] : []) // Remove CRT if capitalized
    ];
    const upfrontTaxItems = shouldShowUpfrontTaxItems ? removeTaxesByName(getFlatOrItemizedTaxes, taxesToRemove) : [];
    const upfrontTaxesDisplay = lineItemWithChildrenAdaptor(upfrontTaxItems, Tax_Object);

    const itemizedTaxes = hasManualUpfrontTotalTax ? manualTaxTotalDisplay : upfrontTaxesDisplay;

    // Lender fees
    const upFrontLenderFeesTotal = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getDiaLenderFees, {
            component: 'DueAtSigning',
            field: 'diaLenderFees'
        })
    );
    const itemizedLenderFees = useTrackedSelector(compositeSelectors.getLenderFeesUpFront, {
        component: 'DueAtSigning',
        field: 'lenderFeesUpFront'
    });

    // Gov fees
    const upfrontGovFeesTotal = useHeldValue(useDealRootSelector(offerReduxSelectors.getDiaGovFees, 'diaGovFees'), deal);
    const upfrontGovFees = useDealRootSelector(compositeSelectors.getGovernmentFeesUpFront, 'governmentFeesUpFront').filter(
        (fee) => fee.amount > 0
    );

    // Dealer fees
    const upfrontDealerFeesTotal = useHeldValue(useDealRootSelector(offerReduxSelectors.getDiaDealerFees, 'diaDealerFees'), deal);
    const upfrontDealerFees = useDealRootSelector(compositeSelectors.getDealerFeesUpfront, 'dealerFeesUpfront').filter(
        (fee) => fee.dealerFeeAmount > 0
    );

    const secDeposit = useDealRootSelector(
        offerSelectors.getSecurityDepositOverrideWithFallback,
        'securityDepositOverrideWithFallback'
    );

    const selectedCapCostReduction = useHeldValue(
        useDealRootSelector(dueAtSigningSelectors.getDasCapCostReduction, 'dasCapCostReduction'),
        deal
    );

    const netCashDown = useHeldValue(useDealRootSelector(offerReduxSelectors.getNetCashDown, 'netCashDown'), deal);
    const reductionRebate = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapReductionRebate, 'capReductionRebate'),
        deal
    );
    const reductionTradeIn = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapReductionTradeIn, 'capReductionTradeIn'),
        deal
    );

    const accessoriesTotal = useHeldValue(useDealRootSelector(offerReduxSelectors.getDiaAccessories, 'diaAccessories'), deal);
    const itemizedAccessories = useDealRootSelector(accessoriesSelectors.getUpfrontAccessoriesList, 'upfrontAccessoriesList').map(
        convertAccessoryToLineItem
    );

    const protectionProductsTotal = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getDiaProtectionProducts, 'diaProtectionProducts'),
        deal
    );
    const vppProducts = useDealRootSelector(vehicleProtectionSelectors.getVppProducts, 'vppProducts');
    const protectionProductsItems: LineItemCategory[] = useMemo(
        () =>
            vppProducts
                .filter(vehicleProtectionUtils.isVppProductSelected)
                .filter(vehicleProtectionUtils.isVppUpfrontProduct)
                .map(convertVppProductToLineItem),
        [vppProducts]
    );

    const isPriorBalanceEnabled = useTrackedSelector(featureToggleSelectors.isPriorBalanceEnabled, {
        component: 'DueAtSigning',
        field: 'priorBalanceEnabled'
    });
    const upfrontPriorBalance = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getUpfrontPriorLeaseBalanceByOfferType, 'upfrontPriorLeaseBalance'),
        deal
    );
    const showUpfrontPriorBalance = isPriorBalanceEnabled && upfrontPriorBalance > 0;

    const labelStyles = showDetailedView ? detailedViewLabelStyles : defaultLabelStyles;
    const valueStyles = showDetailedView ? detailedViewValueStyles : defaultValueStyles;
    const gridProps = showDetailedView && isLargeSize ? detailedViewGridProps : defaultGridProps;

    return (
        <DealSummaryItem
            valueStyles={valueStyles}
            labelStyles={!showDetailedView ? labelStyles : {}}
            label={constants.DUE_AT_SIGNING}
            value={formattedTotalAmountDue}
        >
            <Grid container sx={gridProps}>
                {!firstPaymentCapitalized && (
                    <LineItemWithChildren
                        containerStyles={{ py: 1 }}
                        label={constants.FIRST_MONTHLY_PAYMENT}
                        value={formatDollarsAndCents(firstPaymentAmount)}
                        labelStyles={showDetailedView ? detailedViewLabelStyles : {}}
                        valueStyles={showDetailedView ? secondaryValueStyles : {}}
                    />
                )}
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={getLabelStyles(showDetailedView, itemizedTaxes.length > 0)}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.UPFRONT_TAXES}
                    value={formatDollarsAndCents(upfrontTaxTotal)}
                >
                    {!!itemizedTaxes.length &&
                        itemizedTaxes.map((tax) => (
                            <LineItemWithChildren key={tax.key} label={tax.label} value={formatDollarsAndCents(tax.value)} />
                        ))}
                </DealSummaryItem>
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={getLabelStyles(showDetailedView, itemizedLenderFees.length > 0)}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.LENDER_FEES}
                    value={formatDollarsAndCents(upFrontLenderFeesTotal)}
                >
                    {!!itemizedLenderFees.length &&
                        itemizedLenderFees.map((lender) => (
                            <LineItemWithChildren
                                key={lender.dealerFeeName}
                                label={lender.dealerFeeName}
                                value={formatDollarsAndCents(lender.dealerFeeAmount)}
                            />
                        ))}
                </DealSummaryItem>
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={getLabelStyles(showDetailedView, upfrontGovFees.length > 0)}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.GOVERNMENT_FEES}
                    value={formatDollarsAndCents(upfrontGovFeesTotal)}
                >
                    {!!upfrontGovFees.length &&
                        upfrontGovFees.map((govFees) => (
                            <LineItemWithChildren
                                key={govFees.name}
                                label={govFees.name}
                                value={formatDollarsAndCents(govFees.amount)}
                            />
                        ))}
                </DealSummaryItem>
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={getLabelStyles(showDetailedView, upfrontDealerFees.length > 0)}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.DEALER_FEES}
                    value={formatDollarsAndCents(upfrontDealerFeesTotal)}
                >
                    {!!upfrontDealerFees.length &&
                        upfrontDealerFees.map((dealer) => (
                            <LineItemWithChildren
                                key={dealer.dealerFeeName}
                                label={dealer.dealerFeeName}
                                value={formatDollarsAndCents(dealer.dealerFeeAmount)}
                            />
                        ))}
                </DealSummaryItem>
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={getLabelStyles(showDetailedView, protectionProductsItems.length > 0)}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.PROTECTION_PRODUCTS}
                    value={formatDollarsAndCents(protectionProductsTotal)}
                >
                    {!!protectionProductsItems.length &&
                        protectionProductsItems.map((product) => (
                            <LineItemWithChildren key={product.label} label={product.label} value={product.value} />
                        ))}
                </DealSummaryItem>
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={getLabelStyles(showDetailedView, itemizedAccessories.length > 0)}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.ACCESSORIES}
                    value={formatDollarsAndCents(accessoriesTotal)}
                >
                    {!!itemizedAccessories.length &&
                        itemizedAccessories.map((acc) => (
                            <LineItemWithChildren key={acc.label} label={acc.label} value={acc.value} />
                        ))}
                </DealSummaryItem>
                {showUpfrontPriorBalance && (
                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={getLabelStyles(showDetailedView, false)}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                        label={constants.PRIOR_BALANCE}
                        value={formatDollarsAndCents(upfrontPriorBalance)}
                    />
                )}
                <LineItemWithChildren
                    containerStyles={{ py: 1 }}
                    label={constants.SECURITY_DEPOSIT}
                    value={formatDollarsAndCents(secDeposit)}
                    labelStyles={showDetailedView ? detailedViewLabelStyles : {}}
                    valueStyles={showDetailedView ? secondaryValueStyles : {}}
                />
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={labelStyles}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.CAP_COST_REDUCTION}
                    value={formatDollarsAndCents(selectedCapCostReduction)}
                >
                    <LineItemWithChildren label={constants.CASH_FROM_CUSTOMER} value={formatDollarsAndCents(netCashDown)} />
                    <LineItemWithChildren label={constants.REBATES} value={formatDollarsAndCents(reductionRebate)} />
                    <LineItemWithChildren label={constants.TRADE_IN} value={formatDollarsAndCents(reductionTradeIn)} />
                </DealSummaryItem>
            </Grid>
        </DealSummaryItem>
    );
};

export default DueAtSigning;
